
// eslint-disable-next-line quotes
import { defineStore } from "pinia"
import { useMessageStore } from '@/stores/messages'
import { useUsersStore } from '@/stores/users'
import { useGroupsStore } from "@/stores/groups"
import services from '@/services'
import useWebSocketChat from '@/hooks/useWebSocketChat'
import { ItemMessageChat, UserOrDepartment } from '@/types/itemsMessages'
import { KeysRootStateMessage  } from '@/types/message'
import { ChatItem, groupInternalChat, usersInternalChat } from '@/types/internalChatAxios'
import { orderIdDesc } from '@/helpers/messages';
import { sleep } from './../helpers/index';
import useIframeControl from '@/hooks/iframeInternalChat'

interface Item {
  id: number;
  name: string;
  description?: string;
  quantity: number;
  createdAt: Date;
  deletedAt?: Date;
}
type InterfaceChat = {
    positionX: number;
    positionY: number
}
interface RootState {
  items: Item[] | [];
  selected: ChatItem | null;
  bearer: string;
  token: string;
  updated: Date;
  loading: boolean;
  initLoad: boolean;
  firstInstance: boolean;
  type: 'full' | 'widget'
  useLeft: boolean;
  interface?: InterfaceChat;
}

export const useChatStore = defineStore({
  id: 'chat',
  state: (): RootState => ({
    selected: null,
    bearer: '',
    token: '',
    firstInstance: false,
    items: [],
    updated: new Date(),
    loading: false,
    initLoad: true,
    type: 'widget',
    useLeft: false,
    interface:{
        positionX: 0,
        positionY: 0
    }
  }),
  /*
  */
  getters: {
    all: (state) => state.items,
    config: (state) => {
        return {
            headers: {
                Accept: 'application/json',
                'Content-type': 'application/json',
                Authorization: 'Bearer ' + state.bearer
            }
        }
    },
    getById: (state) => (id: number) => state.items.find((el) => el.id === id),
    getSelected: (state) => state.selected,
    getWith: (state) => state.selected?.selected ?? 'to_user',
    getWithService: (state) => state.selected?.selected === 'to_group' ? 'group' : 'user' ?? 'user',
    getWithMessages: (state) => state.selected?.selected === 'to_group' ? 'groups' : 'users' ?? 'users',
    getPositionInterface: (state) => state?.interface ?? { positionX: 10, positionY: 10},
    getUserOrDepartment: (state) => {
      if (state.selected?.selected === 'to_group') {
        return {
          to_group: state.selected.id,
          to_users: state.selected.to_users
        }
      }
      return {
        to_user: state.selected?.id ?? 0
      }
    },
    isSelected: (state) => (id: number) => {
        if (state.selected?.selected) {
          if (state.selected?.id === id) {
            return true
          }
        }
        return false
      },
    isLoading: (state) => state.loading,

    // getById: (state) => (id: number) => findById(state.items, id)
  },
  /*
  */
  actions: {
    async initialize () {

      const response = await services.internalChat.initialize()
      if(response.errors) {
        const iframe = useIframeControl()
        iframe.notifyKill()
      }
      if (!response.errors) {
        const users = response.data.users?.map(item => {
            item.typing_messages = false;  return item
        }) as usersInternalChat[]
        const groups = response.data.groups?.map(item => {
            item.typing_messages = false,
            item.typing_user = 0
            return item
        }) as groupInternalChat[]

        const userStore = useUsersStore()
        const groupStore = useGroupsStore()

        this.upToken(response.data.websocket.token)
        this.updated = new Date()
        userStore.upUser(response.data.user)
        userStore.upItems(users)
        groupStore.upItems(groups)
      }
      return response
    },
    async syncChat () {
        const response = this.selected?.id ? await services.internalChat.sync() : await services.internalChat.initialize()
        if(response.errors) {
            const iframe = useIframeControl()
            iframe.notifyKill()
        }
        if (!response.errors) {
            const userStore = useUsersStore()
            const groupStore = useGroupsStore()
            this.updated = new Date()
            userStore.upUser(response.data.user)
            userStore.upItems(response.data.users)
            groupStore.upItems(response.data.groups)
            this.checkSelected()
        }
        // console.log(response)
        if(response.data?.selected) {
            // console.log(response.data.selected)
            const messageStore = useMessageStore()
            const id = response.data.selected?.to_user ?? response.data.selected?.to_group ?? 0
            const type = (response.data.selected?.to_user ? 'user'  : 'group') + 's' as KeysRootStateMessage
            const payload = { data : response.data.selected.last_messages }

            messageStore.addMessages(id, payload, type)
        }
        return response
    },

    async fetchMessage (id: number, type : KeysRootStateMessage = 'users') {
      if (!id) {
        throw new Error('not selected user')
      }
      const messageStore = useMessageStore()
      const offset = `?offset=${messageStore.countMessages(id, type)}`
      const sType = this.getWithService
      this.loading = true
      const response = await services[sType].findMessage(id, offset)
      this.loading = false
      if (response.errors) {
        throw new Error('error request findMessageUser')
      }
      if (response.data) {
        const result = { data: orderIdDesc(response.data.data) }
        if(result.data.length > 0) {
            messageStore.addMessages(id, result, ( `${type}` as KeysRootStateMessage))
        }else if(this.selected){
            this.selected.has_more_messages = false
        }
        return result.data
      }
    },

    // envia uma mensagem
    sendMessage(data: ItemMessageChat, destination: UserOrDepartment | null) {
      if (!data && !destination) return
      if(destination?.to_group) {
        const groupStore = useGroupsStore()
        destination.to_users =  groupStore.getUsers(destination.to_group).map(el => el.id)
      }
      const payload = {
        type: 'send_message',
        channel: 'internalChat',
        data: {
          ...destination,
          uuid: data.uuid,
          type: data.message.type,
          message: data.message.content,
          reply_message: data.message.reply,
          extra: data.message.extra
        }
      }
      const { send } = useWebSocketChat()
      send(JSON.stringify(payload))
    },

    // envia uma confirmacao de que a mensagem foi recebida
    confirmationMessage(data : UserOrDepartment) {
      const payload = {
        type: 'read_messages',
        channel: 'internalChat',
        data
      }
      const { send } = useWebSocketChat()
      send(JSON.stringify(payload))
    },

    typingMessage(data : UserOrDepartment, status: boolean = false) {
      const payload = {
        type: 'typing_messages',
        channel: 'internalChat',
        data: {
          status,
          type: 'typing',
          ...data
        }
      }
      const { send } = useWebSocketChat()
      send(JSON.stringify(payload))
    },

    resetSelected() {
      this.selected = null
    },
    // verifico se depois da atualizacao o item selecionado ainda existe
    checkSelected() {
        if(this.getSelected && this.getWith === 'to_user') {
            const userStore = useUsersStore()
            if(!userStore.getById(this.getSelected.id)){
                this.resetSelected()
            }
        }
        if(this.getSelected && this.getWith === 'to_group') {
            const groupStore = useGroupsStore()
            if(!groupStore.getById(this.getSelected.id)) {
                this.resetSelected()
            }
        }
    },
    // inicializo as mensagens quando mudo de conversa
    async upSelected (selected : ChatItem) {
        if (!selected) return
        if (this.selected === selected) return
        this.initLoad = true
        this.selected = selected
        this.confirmationMessage(this.getUserOrDepartment)
        const messageStore = useMessageStore()
        const isWith = this.getWithMessages
        if(messageStore[isWith][selected.id].data.length <= 10) {
            if(isWith === 'users'){
                const result = await this.fetchMessage(selected.id)
                if (result && result.length > 0) {
                    const userStore = useUsersStore()
                    userStore.readMessage(selected.id)
                    userStore.upLastMessage(selected.id)
                }
            }
            if(isWith === 'groups') {
                const result = await this.fetchMessage(selected.id, 'groups')
                if (result && result.length > 0) {
                    const groupStore = useGroupsStore()
                    groupStore.readMessage(selected.id)
                    groupStore.upLastMessage(selected.id)
                }
            }
        }else{
            await sleep(500)
        }
        this.initLoad = false
    },

    upBearer (bearer : string) {
      this.bearer = bearer
    },

    upToken (token : string) {
      this.token = token
    },

    upWebsocketStatus (value : number) {
      console.log(value)
    },

    upFirstInstance (value : boolean) {
      if (!value) return
      this.firstInstance = value
    },

    upInterface (value: InterfaceChat) {
        if(!value) return
        this.interface = value
    }
  }
})
